import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "@/utils/theme";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return (
    <DialogPrimitive.Close
      className="text-body-loud"
      data-slot="dialog-close"
      {...props}
    />
  );
}

function DialogOverlay({
  className,
  type,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay> & {
  type: "side" | "modal" | "full" | "none";
}) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-[#00000060]",
        type === "full" &&
          "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out bg-neutral-object-overlay-loud",
        type === "side" &&
          "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
        type === "modal" &&
          "data-[state=open]:animate-slide-to-up data-[state=closed]:animate-slide-to-down",
        type === "none" && "bg-transparent",
        className,
      )}
      {...props}
      style={{
        ...props.style,
        ...(type === "full" && {
          backdropFilter: "blur(32px)",
          WebkitBackdropFilter: "blur(32px)",
        }),
      }}
    />
  );
}

type DialogContentProps = React.ComponentProps<
  typeof DialogPrimitive.Content
> & {
  type?: "side" | "modal" | "full";
  overlay?: boolean;
  showCloseButton?: boolean;
  controlers?: React.ReactNode;
};

function DialogContent({
  className,
  children,
  type = "modal",
  overlay = true,
  showCloseButton = true,
  controlers,
  ...props
}: DialogContentProps) {
  if (type === "full") {
    return (
      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay type={overlay ? type : "none"} />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "bg-neutral-foreground gap-4 rounded-t-lg shadow-custom duration-200",
            "absolute top-16 inset-x-4 z-50 grid w-[calc(100%-2rem)]",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
            "h-[calc(100%-4rem)]",
            "flex flex-col",
            className,
          )}
          {...props}
        >
          <div className="w-full h-10 absolute -top-12 left-0 flex items-center justify-end gap-2">
            {controlers && controlers}
          </div>
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
              <XIcon />
              <span className="sr-only">Cerrar</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }

  if (type === "side") {
    return (
      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay type={overlay ? type : "none"} />
        {/* "bg-neutral-foreground gap-4 rounded-l-lg p-8 shadow-custom
        duration-200", "fixed right-2.5 top-[70px] z-50 grid w-full",
        "data-[state=open]:animate-fade-in
        data-[state=closed]:animate-fade-out", "h-full", "flex flex-col", */}
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "bg-neutral-foreground gap-4 rounded-l-lg p-8 shadow-custom duration-200",
            "fixed right-0 top-0 z-50 grid w-full",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
            "h-full",
            "flex flex-col",
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
              <XIcon />
              <span className="sr-only">Cerrar</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay type={overlay ? type : "none"} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-neutral-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg p-8 shadow-custom duration-200",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close className="ring-offset-background text-body-loud focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
            <XIcon />
            <span className="sr-only">Cerrar</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "text-lg leading-none font-semibold text-heading",
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
